import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { apiService } from '../services/api';
import authService from '../services/authApi';
import logger from '../utils/logger';
import { orthoTrack, identifyUser, resetUser } from '../utils/posthog';

interface AuthUser {
  user_id: string;
  email: string;
  name: string;
  role: string;
  email_verified?: boolean;
}

interface AuthContextType {
  user: AuthUser | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  signIn: (email: string, password: string) => Promise<void>;
  signUp: (email: string, password: string, name: string) => Promise<void>;
  signOut: () => Promise<void>;
  resetPassword: (email: string) => Promise<void>;
  updatePassword: (newPassword: string) => Promise<void>;
  refreshToken: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  // Check if user is authenticated on mount
  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    try {
      const token = localStorage.getItem('access_token');
      if (!token) {
        setIsLoading(false);
        return;
      }

      // Verify token with backend
      const userData = await apiService.getCurrentAuthUser(token);
      if (userData) {
        setUser({
          user_id: userData.id,
          email: userData.email,
          name: userData.name,
          role: userData.role || 'orthodontist',
          email_verified: false
        });
        setIsAuthenticated(true);
        // IMPORTANT: Set user ID in API service for session creation
        apiService.setAuthToken(token);
        apiService.setUserId(userData.id);
      }
    } catch (error) {
      logger.error('Auth check failed:', error);
      // Token might be expired, try to refresh
      await refreshToken();
    } finally {
      setIsLoading(false);
    }
  };

  const signIn = async (email: string, password: string) => {
    try {
      const response = await authService.signIn(email, password);
      
      // Store tokens
      localStorage.setItem('access_token', response.access_token);
      localStorage.setItem('refresh_token', response.refresh_token);
      localStorage.setItem('user_id', response.user_id);
      
      // Set user data
      setUser({
        user_id: response.user_id,
        email: response.email,
        name: response.name || email.split('@')[0],
        role: response.role || 'orthodontist',
        email_verified: response.email_verified
      });
      setIsAuthenticated(true);
      
      // Update API service with the token
      apiService.setAuthToken(response.access_token);
      apiService.setUserId(response.user_id);
      
      logger.log('Sign in successful');
    } catch (error: any) {
      logger.error('Sign in failed:', error);
      throw new Error(error.response?.data?.detail || 'Sign in failed');
    }
  };

  const signUp = async (email: string, password: string, name: string) => {
    try {
      const response = await authService.signUp(email, password, name, 'orthodontist');
      
      // Check if email verification is required
      if (response.requires_email_verification) {
        logger.log('Email verification required');
        throw new Error('Please check your email to verify your account before signing in.');
      }
      
      // If no verification required, automatically sign in
      if (response.access_token) {
        localStorage.setItem('access_token', response.access_token);
        localStorage.setItem('refresh_token', response.refresh_token || '');
        localStorage.setItem('user_id', response.user_id || '');
        
        setUser({
          user_id: response.user_id || '',
          email: response.email || email,
          name: name,
          role: 'orthodontist',
          email_verified: false
        });
        setIsAuthenticated(true);
        
        apiService.setAuthToken(response.access_token);
        apiService.setUserId(response.user_id || '');
        
        // Track sign up event
        orthoTrack.userSignedUp('email');
        identifyUser(response.user_id || '', {
          email: response.email || email,
          name: name,
          role: 'orthodontist'
        });
      }
      
      logger.log('Sign up successful');
    } catch (error: any) {
      logger.error('Sign up failed:', error);
      throw new Error(error.response?.data?.detail || error.message || 'Sign up failed');
    }
  };

  const signOut = async () => {
    try {
      const token = localStorage.getItem('access_token');
      if (token) {
        await authService.signOut(token);
      }
    } catch (error) {
      logger.error('Sign out error:', error);
    } finally {
      // Clear local storage
      localStorage.removeItem('access_token');
      localStorage.removeItem('refresh_token');
      localStorage.removeItem('user_id');
      
      // Clear state
      setUser(null);
      setIsAuthenticated(false);
      
      // Clear API service token
      apiService.clearAuth();
      
      // Track sign out and reset PostHog user
      orthoTrack.userSignedOut();
      resetUser();
      
      logger.log('Signed out');
    }
  };

  const resetPassword = async (email: string) => {
    try {
      await authService.resetPassword(email);
      logger.log('Password reset email sent');
    } catch (error: any) {
      logger.error('Password reset failed:', error);
      throw new Error(error.response?.data?.detail || 'Failed to send reset email');
    }
  };

  const updatePassword = async (newPassword: string) => {
    try {
      const token = localStorage.getItem('access_token');
      if (!token) {
        throw new Error('Not authenticated');
      }
      
      await authService.updatePassword(token, newPassword);
      logger.log('Password updated successfully');
    } catch (error: any) {
      logger.error('Password update failed:', error);
      throw new Error(error.response?.data?.detail || 'Failed to update password');
    }
  };

  const refreshToken = async () => {
    try {
      const refresh_token = localStorage.getItem('refresh_token');
      if (!refresh_token) {
        throw new Error('No refresh token');
      }

      const response = await authService.refreshToken(refresh_token);
      
      // Update tokens
      localStorage.setItem('access_token', response.access_token);
      localStorage.setItem('refresh_token', response.refresh_token);
      
      // Update API service
      apiService.setAuthToken(response.access_token);
      
      // Get updated user data
      await checkAuth();
      
      logger.log('Token refreshed');
    } catch (error) {
      logger.error('Token refresh failed:', error);
      // Clear auth on refresh failure
      await signOut();
    }
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        isLoading,
        isAuthenticated,
        signIn,
        signUp,
        signOut,
        resetPassword,
        updatePassword,
        refreshToken,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};